import { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { Check } from 'lucide-react'

const PRESETS = ['Black Tie','Black Tie Optional','Formal','Semi-Formal','Smart Casual','Casual','Traditional / Aso-Ebi','Beach Formal','Cocktail Attire','Agbada / Native Attire','Sunday Best']

export default function DressCodeTab({ event }) {
  const [dressCode, setDressCode] = useState('')
  const [custom, setCustom]       = useState(false)   // "Other — type your own"
  const [fabric, setFabric]       = useState('')
  const [notes, setNotes]         = useState('')
  const [saved, setSaved]         = useState(false)
  const [saving, setSaving]       = useState(false)
  const [loading, setLoading]     = useState(true)

  // Load existing dress code data when tab opens
  useEffect(() => {
    async function load() {
      if (!event?.id) return
      const { data } = await supabase
        .from('wedding_events')
        .select('dress_code, dress_code_fabric, dress_code_notes')
        .eq('id', event.id)
        .single()
      if (data) {
        setDressCode(data.dress_code || '')
        // A saved value that isn't one of the presets is a custom entry
        setCustom(!!data.dress_code && !PRESETS.includes(data.dress_code))
        setFabric(data.dress_code_fabric || '')
        setNotes(data.dress_code_notes || '')
      }
      setLoading(false)
    }
    load()
  }, [event?.id])

  async function save() {
    setSaving(true)
    const { error } = await supabase.from('wedding_events').update({
      dress_code: dressCode,
      dress_code_fabric: fabric,
      dress_code_notes: notes,
      dress_code_colors: [],   // colour palette retired
    }).eq('id', event.id)
    if (!error) { setSaved(true); setTimeout(()=>setSaved(false), 2500) }
    setSaving(false)
  }

  if (loading) return <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC', padding:'2rem' }}>Loading…</p>

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem', maxWidth:520 }}>
      <div>
        <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', margin:'0 0 0.2rem' }}>Dress Code</h2>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:0 }}>
          Set the dress code for <strong>{event?.type}</strong>. It's automatically included in the invites and reminders you send your guests.
        </p>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
        <div>
          <label className="label">Dress Code Style</label>
          <select
            className="input-field"
            value={custom ? '__custom__' : dressCode}
            onChange={e => {
              if (e.target.value === '__custom__') { setCustom(true); setDressCode('') }
              else { setCustom(false); setDressCode(e.target.value) }
            }}>
            <option value="">Select dress code…</option>
            {PRESETS.map(d => <option key={d}>{d}</option>)}
            <option value="__custom__">Other — type your own…</option>
          </select>
          {custom && (
            <input
              className="input-field"
              placeholder="Type your dress code (e.g. All White, Owambe Glam)"
              value={dressCode}
              onChange={e => setDressCode(e.target.value)}
              autoFocus
              style={{ marginTop:'0.5rem' }}
            />
          )}
        </div>
        <div>
          <label className="label">Fabric / Material</label>
          <input className="input-field" placeholder="e.g. Royal Blue Ankara, White Aso-oke" value={fabric} onChange={e=>setFabric(e.target.value)}/>
        </div>
        <div>
          <label className="label">Additional Notes</label>
          <textarea className="input-field" rows={3} placeholder="e.g. Ladies are encouraged to wear Aso-Ebi. Gents: white agbada or suit. No black please." value={notes} onChange={e=>setNotes(e.target.value)} style={{ resize:'vertical' }}/>
        </div>
        <button className="btn-primary" onClick={save} disabled={saving} style={{ display:'flex', alignItems:'center', gap:'0.5rem', justifyContent:'center' }}>
          {saved?<><Check size={15}/> Saved!</>:saving?'Saving…':'Save Dress Code'}
        </button>

        <div style={{ background:'rgba(155,175,155,0.08)', border:'1px solid rgba(155,175,155,0.25)', borderRadius:'0.875rem', padding:'0.875rem 1rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#1A1A1A', fontWeight:600, margin:'0 0 0.2rem' }}>✓ Added to guest messages</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.73rem', color:'#6B6B6B', margin:0, lineHeight:1.5 }}>
            This dress code is included — neatly under <strong>{event?.type}</strong> — in the invites and reminders you send from the Guests &amp; RSVP page.
          </p>
        </div>
      </div>
    </div>
  )
}
